import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Info, ArrowRight, Clock } from "lucide-react";
import { EcGift as Gift } from "@/components/icons/EcGift";
import { EcMark } from "@/components/icons/EcMark";
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useLocation, useNavigate } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { useLanguage } from '@/contexts/LanguageContext';
import { getAuthUser } from "@/lib/authUser";

interface PromoPopup {
  id: string;
  title: string;
  description: string | null;
  popup_type: string;
  display_mode: string;
  cta_text: string | null;
  cta_link: string | null;
  discount_percent: number | null;
  discount_duration_hours: number | null;
  trigger_pages: string[] | null;
  background_color: string | null;
  is_active: boolean;
  priority: number | null;
  target_audience: 'all' | 'visitors' | 'accounts';
}

const POPUP_SESSION_PREFIX = 'eyecaptain_popup_session_';
const OFFER_DISCOUNT_COOKIE = 'eyecaptain_popup_offer_discount';

// Session storage utilities (for once per session)
const setSessionShown = (popupId: string) => {
  sessionStorage.setItem(`${POPUP_SESSION_PREFIX}${popupId}`, 'true');
};

const hasSessionShown = (popupId: string): boolean => {
  return sessionStorage.getItem(`${POPUP_SESSION_PREFIX}${popupId}`) === 'true';
};

// Cookie utilities for discount
const setCookie = (name: string, value: string, hours: number) => {
  const date = new Date();
  date.setTime(date.getTime() + hours * 60 * 60 * 1000);
  document.cookie = `${name}=${value}; expires=${date.toUTCString()}; path=/; SameSite=Lax`;
};

const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  return null;
};

export function PromoPopup() {
  const [popup, setPopup] = useState<PromoPopup | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [showExitIntent, setShowExitIntent] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { language } = useLanguage();

  // Check auth status on mount
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setIsLoggedIn(!!session);
    };
    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      setIsLoggedIn(!!session);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Fetch active popups
  const fetchActivePopup = useCallback(async () => {
    // Wait for auth status to be determined
    if (isLoggedIn === null) return;

    const currentPath = location.pathname;
    
    const { data: popups } = await supabase
      .from('promo_popups')
      .select('*')
      .eq('is_active', true)
      // 'extension' rows are the banner in EyeCaptain for Chrome, never a website popup.
      .neq('display_mode', 'extension')
      .order('priority', { ascending: false });

    if (!popups || popups.length === 0) return;

    // Find matching popup for current page
    const matchingPopup = popups.find((p) => {
      // Check if already shown in this session
      if (hasSessionShown(p.id)) return false;

      // Check target audience
      const targetAudience = (p.target_audience || 'all') as 'all' | 'visitors' | 'accounts';
      if (targetAudience === 'visitors' && isLoggedIn) return false;
      if (targetAudience === 'accounts' && !isLoggedIn) return false;

      // Check trigger pages
      if (p.trigger_pages && p.trigger_pages.length > 0) {
        return p.trigger_pages.some(page => 
          currentPath === page || currentPath.startsWith(page)
        );
      }
      // If no trigger pages, show on all pages
      return true;
    });

    if (matchingPopup) {
      setPopup({
        ...matchingPopup,
        target_audience: (matchingPopup.target_audience || 'all') as 'all' | 'visitors' | 'accounts'
      });
      
      // Mark as shown in this session immediately
      setSessionShown(matchingPopup.id);
      
      // Handle display mode
      if (matchingPopup.display_mode === 'exit') {
        // Set up exit intent listener (only on desktop, exit intent doesn't work on mobile)
        if (!isMobile) {
          setShowExitIntent(true);
        }
      } else {
        // Show with delay - longer delay on mobile for better performance
        const delay = isMobile ? 5000 : 1000;
        setTimeout(() => setIsVisible(true), delay);
      }

      // Track view
      const { data: { user } } = await getAuthUser();
      await supabase.from('promo_popup_views').insert({
        popup_id: matchingPopup.id,
        user_id: user?.id || null,
        session_id: !user ? `session_${Date.now()}` : null,
      });
    }
  }, [location.pathname, isLoggedIn, isMobile]);

  // Exit intent handler
  useEffect(() => {
    if (!showExitIntent || !popup) return;

    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0) {
        setIsVisible(true);
        setShowExitIntent(false);
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [showExitIntent, popup]);

  useEffect(() => {
    fetchActivePopup();
  }, [fetchActivePopup]);

  const handleDismiss = async () => {
    if (!popup) return;
    
    setIsVisible(false);

    // Track dismissal
    const { data: { user } } = await getAuthUser();
    await supabase.from('promo_popup_views').insert({
      popup_id: popup.id,
      user_id: user?.id || null,
      session_id: !user ? `session_${Date.now()}` : null,
      dismissed: true,
    });
  };

  const handleCTAClick = async () => {
    if (!popup) return;

    // If it's an offer, save discount to cookie
    if (popup.popup_type === 'offer' && popup.discount_percent) {
      const discountData = {
        popupId: popup.id,
        discountPercent: popup.discount_percent,
        expiresAt: Date.now() + (popup.discount_duration_hours || 1) * 60 * 60 * 1000,
      };
      setCookie(OFFER_DISCOUNT_COOKIE, JSON.stringify(discountData), popup.discount_duration_hours || 1);
    }

    // Track CTA click
    const { data: { user } } = await getAuthUser();
    await supabase.from('promo_popup_views').insert({
      popup_id: popup.id,
      user_id: user?.id || null,
      session_id: !user ? `session_${Date.now()}` : null,
      clicked_cta: true,
    });

    // Navigate if direction type
    if (popup.popup_type === 'direction' && popup.cta_link) {
      navigate(popup.cta_link);
    }

    setIsVisible(false);
  };

  if (!popup || !isVisible) return null;

  const Icon = popup.popup_type === 'offer' ? Gift : popup.popup_type === 'direction' ? ArrowRight : Info;

  // Render based on display mode
  const renderPopup = () => {
    const content = (
      <>
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 p-1 rounded-full bg-white/20 hover:bg-white/30 text-white/80 hover:text-white transition-all duration-200"
        >
          <X className="w-4 h-4" />
        </button>
        
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
            <Icon className="w-5 h-5 text-white" />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <EcMark className="w-4 h-4 text-[hsl(var(--warning))]" />
              <span className="text-xs font-medium text-[hsl(var(--warning))] uppercase tracking-wide">
                {language === 'el' ? 'Ειδική Προσφορά' : 'Special Offer'}
              </span>
            </div>
            <h3 className="text-lg font-bold text-white mb-1">{popup.title}</h3>
            {popup.description && (
              <p className="text-white/80 text-sm mb-4">{popup.description}</p>
            )}
            
            {popup.popup_type === 'offer' && popup.discount_percent && (
              <div className="flex items-center gap-3 mb-4 p-3 rounded-lg bg-white/15 backdrop-blur-sm">
                <span className="text-2xl font-bold text-white">{popup.discount_percent}%</span>
                <div className="flex-1">
                  <span className="text-white text-sm font-medium">
                    {language === 'el' ? 'έκπτωση' : 'discount'}
                  </span>
                  <div className="flex items-center gap-1 text-white/70 text-xs">
                    <Clock className="w-3 h-3" />
                    <span>
                      {language === 'el'
                        ? `Ισχύει για ${popup.discount_duration_hours || 1} ώρες`
                        : `Valid for ${popup.discount_duration_hours || 1} hours`}
                    </span>
                  </div>
                </div>
              </div>
            )}
            
            {popup.cta_text && (
              <Button
                onClick={handleCTAClick}
                className="w-full sm:w-auto bg-card hover:bg-white/90 text-[hsl(var(--warning))] font-semibold shadow-lg"
              >
                {popup.cta_text}
              </Button>
            )}
          </div>
        </div>
      </>
    );

    // No backdrop-blur on mobile for performance
    const cardClass = isMobile 
      ? "bg-gradient-to-br from-primary via-primary to-[hsl(var(--ec-mint))] border border-[hsl(var(--warning)/0.3)] shadow-2xl"
      : "bg-gradient-to-br from-primary via-primary to-[hsl(var(--ec-mint))] backdrop-blur-xl border border-[hsl(var(--warning)/0.3)] shadow-2xl";

    switch (popup.display_mode) {
      case 'side':
        return (
          <motion.div
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 100, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className={`fixed bottom-6 right-6 z-50 max-w-sm rounded-2xl p-6 ${cardClass}`}
          >
            {content}
          </motion.div>
        );
      
      case 'bottom':
        return (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className={`fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6 ${cardClass} rounded-t-2xl`}
          >
            <div className="max-w-4xl mx-auto relative">
              {content}
            </div>
          </motion.div>
        );
      
      case 'fullpage':
        return (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className={`max-w-md w-full rounded-2xl p-8 ${cardClass}`}
            >
              {content}
            </motion.div>
          </motion.div>
        );
      
      case 'exit':
        return (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4"
          >
            <motion.div
              initial={{ scale: 0.95, y: -20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.95, y: -20, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className={`max-w-md w-full rounded-2xl p-8 ${cardClass}`}
            >
              {content}
            </motion.div>
          </motion.div>
        );
      
      default:
        return null;
    }
  };

  return <AnimatePresence>{renderPopup()}</AnimatePresence>;
}

// Hook to get popup offer discount
export function usePopupOfferDiscount() {
  const [discount, setDiscount] = useState<{ percent: number; expiresAt: number } | null>(null);

  useEffect(() => {
    const cookieValue = getCookie(OFFER_DISCOUNT_COOKIE);
    if (cookieValue) {
      try {
        const data = JSON.parse(cookieValue);
        if (data.expiresAt > Date.now()) {
          setDiscount({ percent: data.discountPercent, expiresAt: data.expiresAt });
        }
      } catch (e) {
        console.error('Error parsing offer discount cookie:', e);
      }
    }
  }, []);

  const calculateDiscountedPrice = useCallback((price: number) => {
    if (!discount) return price;
    return price * (1 - discount.percent / 100);
  }, [discount]);

  return { discount, hasDiscount: !!discount, calculateDiscountedPrice };
}
